import type { ReactElement } from 'react';
import { Group, Stack, Text, UnstyledButton } from '@mantine/core';

interface SessionUtilityBarProps {
  sessionEmail?: string;
  showHome?: boolean;
  onGoHome?: () => void;
  onLogout?: () => void;
  isLoggingOut?: boolean;
  showSampleData?: boolean;
  onFillSampleData?: () => void;
  showRequiredFields?: boolean;
}

export default function SessionUtilityBar({
  sessionEmail,
  showHome = false,
  onGoHome,
  onLogout,
  isLoggingOut = false,
  showSampleData = false,
  onFillSampleData,
  showRequiredFields = false,
}: SessionUtilityBarProps) {
  const actionItems: Array<{ key: string; content: ReactElement }> = [];

  const actionTextStyle = {
    fontSize: 'var(--mantine-font-size-xs)',
    color: 'var(--primary-color)',
    fontWeight: 600,
    lineHeight: 1.2,
  } as const;

  if (showHome && onGoHome) {
    actionItems.push(
      {
        key: 'home',
        content: (
          <UnstyledButton onClick={onGoHome} style={actionTextStyle}>
            Home
          </UnstyledButton>
        ),
      },
    );
  }

  if (showSampleData && onFillSampleData) {
    actionItems.push(
      {
        key: 'sample',
        content: (
          <UnstyledButton onClick={onFillSampleData} style={actionTextStyle}>
            Fill Sample Data
          </UnstyledButton>
        ),
      },
    );
  }

  if (sessionEmail) {
    actionItems.push(
      {
        key: 'email',
        content: (
          <Text size="xs" c="dimmed">
            Logged in as: {sessionEmail}
          </Text>
        ),
      },
    );
  }

  if (sessionEmail && onLogout) {
    actionItems.push(
      {
        key: 'logout',
        content: (
          <UnstyledButton onClick={onLogout} disabled={isLoggingOut} style={actionTextStyle}>
            Logout
          </UnstyledButton>
        ),
      },
    );
  }

  const requiredFieldsNote = showRequiredFields ? (
    <Text size="xs" c="dimmed" ta="center">
      <Text component="span" c="red">*</Text> indicates required fields
    </Text>
  ) : null;

  if (actionItems.length === 0 && !requiredFieldsNote) {
    return null;
  }

  const actions = actionItems.length > 0 ? (
    <Group justify="center" align="center" gap="xs" wrap="wrap" w="100%">
      {actionItems.map((item, index) => (
        <Group key={item.key} gap="xs" align="center" wrap="nowrap">
          {index > 0 && <Text size="xs" c="dimmed">|</Text>}
          {item.content}
        </Group>
      ))}
    </Group>
  ) : null;

  if (!requiredFieldsNote) {
    return actions;
  }

  return (
    <Stack gap={4} align="stretch" w="100%">
      {actions}
      {requiredFieldsNote}
    </Stack>
  );
}